<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div class="card-body">
            <!-- 시스템 저장 안 함 — 나중에 출고 청구 자동생성으로 대체할 임시 도구(2026-10-07 YZY) -->
            <div class="border rounded py-2 px-3 small mb-3 bg-light">
              上传含「账单模板」和「LCL」两个表的Excel，按LCL每个客户一行生成一个账单文件。只在浏览器里生成，不保存到系统。
              (청구서 서식 시트와 LCL 시트가 있는 엑셀을 올리면 LCL 의 고객 행마다 청구서 파일 1개를 만듭니다. 브라우저에서만 만들고 시스템에는 저장하지 않습니다.)
              <div class="text-muted mt-1">
                模板里的【列名】会按LCL表头同名列填入(서식의 【열이름】 자리는 LCL 헤더의 같은 이름 열 값으로 채웁니다). 找不到的列·空值 = 空白，0 = 0(못 찾은 열·빈 값은 빈칸, 0 은 0).
              </div>
            </div>

            <div class="border rounded p-2 mb-3 bg-light" @dragover.prevent @drop.prevent="onDrop">
              <div class="d-flex align-items-center flex-wrap lcl-bill__gap">
                <span class="font-weight-bold">⬆ 上传Excel(엑셀 올리기)</span>
                <label class="btn btn-sm btn-primary mb-0">
                  选择文件(파일 선택)
                  <input ref="file" type="file" accept=".xlsx" class="d-none" @change="onPick">
                </label>
                <span v-if="fileName" class="small">{{ fileName }}</span>
              </div>
              <div v-if="error" class="text-danger small mt-2">⚠ {{ error }}</div>
            </div>

            <template v-if="parsed">
              <div class="small mb-2">
                模板(서식): <b>{{ parsed.template }}</b> · 数据(데이터): <b>{{ parsed.data }}</b>
                <span v-if="parsed.rate !== null"> · 适用汇率(적용환율): <b>{{ parsed.rate }}</b></span>
                · 客户(고객) <b>{{ parsed.rows.length }}</b>
                · 占位符(자리표시자) <b class="text-success">{{ parsed.mapping.matched.length }}</b> 对应(매핑)
                <b :class="parsed.mapping.unmatched.length ? 'text-danger' : 'text-muted'">{{ parsed.mapping.unmatched.length }}</b> 未对应(못 찾음)
              </div>
              <div v-if="parsed.mapping.unmatched.length" class="alert alert-warning py-1 small">
                LCL表头里没有这些列，账单里会留空(LCL 헤더에 없는 열 — 빈칸으로 나갑니다):
                <span v-for="p in parsed.mapping.unmatched" :key="'u' + p" class="badge badge-light border mr-1">【{{ p }}】</span>
              </div>

              <div class="d-flex align-items-center flex-wrap lcl-bill__gap mb-2">
                <b-form-input v-model="search" size="sm" class="lcl-bill__search" placeholder="搜索客户(고객 검색)"></b-form-input>
                <b-button size="sm" variant="primary" :disabled="!checkedRows.length || busy" @click="downloadChecked">
                  下载选中(선택 다운로드) {{ checkedRows.length }}{{ checkedRows.length > 1 ? ' · zip' : '' }}
                </b-button>
                <label class="mb-0 small text-nowrap"><input type="checkbox" v-model="keepFormulas"> 保留公式(수식 유지) — 不勾选则公式转为数值(체크 안 하면 값으로 변환)</label>
                <b-spinner v-if="busy" small></b-spinner>
                <span v-if="message" class="small text-success">{{ message }}</span>
              </div>

              <table class="table table-sm table-bordered small bg-white">
                <thead class="thead-light">
                  <tr>
                    <th style="width:32px"><input type="checkbox" :checked="allChecked" @change="toggleAll($event.target.checked)"></th>
                    <th>No.(客户 고객)</th>
                    <th>日期(날짜)</th>
                    <th v-if="totalCol >= 0" class="text-right">合计(합계)</th>
                    <th>文件名(파일 이름)</th>
                    <th style="width:110px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="r in visibleRows" :key="'r' + r.sheetRow">
                    <td><input type="checkbox" v-model="checked[r.sheetRow]"></td>
                    <td>{{ r.name }}</td>
                    <td>{{ dateOf(r) }}</td>
                    <td v-if="totalCol >= 0" class="text-right">{{ totalOf(r) }}</td>
                    <td class="text-muted">{{ fileNameOf(r) }}</td>
                    <td><b-button size="sm" variant="outline-primary" class="text-nowrap" :disabled="busy" @click="downloadOne(r)">下载(받기)</b-button></td>
                  </tr>
                </tbody>
              </table>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { coupassMenuTitle } from './coupassMenu';
import { parseBillWorkbook, buildCustomerBill, billFileName, zipFileName, rowDate, phKey } from './lclBillXlsx';
import { zipFilesUtf8 } from './cooXlsx';

// 통합통관 청구서 나누기(2026-10-07 YZY) — LCL 표 고객 행마다 청구서 서식을 채워 고객별 파일.
// ⚠ 시스템 저장 안 함 — 나중에 출고 청구 자동생성으로 대체할 임시 도구. 값 채우기·서식 보존은 lclBillXlsx.js
export default {
  name: 'LclBillSplit',
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.lclbillsplit'),
      fileName: '',
      u8: null,
      parsed: null,
      checked: {},
      keepFormulas: false, // 기본 = 수식을 값으로 고정. 체크하면 수식 그대로
      search: '',
      busy: false,
      error: '',
      message: ''
    };
  },
  computed: {
    visibleRows() {
      if (!this.parsed) return [];
      const q = this.search.trim().toLowerCase();
      return q ? this.parsed.rows.filter(r => r.name.toLowerCase().includes(q)) : this.parsed.rows;
    },
    checkedRows() {
      return this.parsed ? this.parsed.rows.filter(r => this.checked[r.sheetRow]) : [];
    },
    allChecked() {
      return this.visibleRows.length > 0 && this.visibleRows.every(r => this.checked[r.sheetRow]);
    },
    totalCol() {
      return this.parsed ? this.parsed.header.findIndex(h => phKey(h) === '합계' || phKey(h) === '合计') : -1;
    }
  },
  methods: {
    onPick(e) {
      const f = e.target.files && e.target.files[0];
      e.target.value = '';
      if (f) this.load(f);
    },
    onDrop(e) {
      const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) this.load(f);
    },
    async load(file) {
      this.error = '';
      this.message = '';
      this.parsed = null;
      this.checked = {};
      this.fileName = file.name;
      try {
        const buf = await file.arrayBuffer();
        this.u8 = new Uint8Array(buf);
        const parsed = parseBillWorkbook(this.u8);
        const checked = {};
        parsed.rows.forEach(r => { checked[r.sheetRow] = true; });
        this.checked = checked;
        this.parsed = parsed;
        if (!parsed.rows.length) this.error = 'LCL表里没有客户行(LCL 시트에 고객 행이 없습니다)';
      } catch (err) {
        console.error('[LclBillSplit] 파일 읽기 실패', err);
        this.error = (err && err.message) || String(err);
      }
    },
    dateOf(r) {
      return rowDate(r, this.parsed.header);
    },
    fileNameOf(r) {
      return billFileName(r, this.fileName);
    },
    totalOf(r) {
      const c = r.cells[this.totalCol];
      if (!c || c.v === '' || c.v === null || c.v === undefined) return '';
      return typeof c.v === 'number' ? c.v.toLocaleString() : String(c.w || c.v);
    },
    toggleAll(on) {
      const next = Object.assign({}, this.checked);
      this.visibleRows.forEach(r => { next[r.sheetRow] = on; });
      this.checked = next;
    },
    saveFile(data, name, type) {
      const blob = new Blob([data], { type });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    },
    downloadOne(r) {
      this.make([r]);
    },
    downloadChecked() {
      this.make(this.checkedRows);
    },
    // 1개면 xlsx 그대로, 여러 개면 zip 하나(연속 다운로드는 브라우저가 막는 경우가 있다)
    make(rows) {
      if (!rows.length) return;
      this.busy = true;
      this.error = '';
      this.message = '';
      try {
        const seen = {};
        const files = rows.map(r => {
          // 같은 고객명이 두 줄이면 zip 안에서 덮어쓰이지 않게 (2) 를 붙인다
          let name = this.fileNameOf(r);
          seen[name] = (seen[name] || 0) + 1;
          if (seen[name] > 1) name = name.replace(/(\.[^.]+)?$/, '(' + seen[name] + ')$1');
          return { name, data: buildCustomerBill(this.u8, this.parsed, r, this.keepFormulas) };
        });
        if (files.length === 1) {
          this.saveFile(files[0].data, files[0].name, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        } else {
          this.saveFile(zipFilesUtf8(files), zipFileName(this.fileName), 'application/zip');
        }
        this.message = '✓ ' + files.length + ' 个文件(파일)';
      } catch (err) {
        console.error('[LclBillSplit] 청구서 만들기 실패', err);
        this.error = (err && err.message) || String(err);
      } finally {
        this.busy = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
.lcl-bill__gap { gap: 8px; }
.lcl-bill__search { max-width: 220px; }
</style>
